import type { InspectionLevel, InspectionLocation } from '@areapif/areapif-shared'
import polyglotI18nProvider from 'ra-i18n-polyglot'
import type { I18nProvider, TranslationMessages } from 'react-admin'

import type { QualifiedStatusId } from '../domain/activeInspection'
import catalanMessages from './ra-language-catalan'
import spanishMessages from './ra-language-spanish'

export const availableLocales = [
  { locale: 'es', name: 'Castellano' },
  { locale: 'ca', name: 'Català' },
] as const
export const LOCALES = availableLocales.map(({ locale }) => locale)
export type Locale = (typeof LOCALES)[number]

export const isLocaleSupported = (locale: string): locale is Locale => {
  return LOCALES.includes(locale as Locale)
}

export const getTranslations = (locale: string): Translations => {
  if (!isLocaleSupported(locale)) {
    throw new Error(`Unsupported locale: ${locale}`)
  }
  switch (locale) {
    case 'es':
      return es
    case 'ca':
      return ca
  }
}

export interface Translations extends TranslationMessages {
  pages: {
    inspections: {
      /**
       * Copy for the hard delete of a manual inspection. Distinct from
       * `historical_inspections.delete`, which is a soft delete on the archive:
       * this one destroys the row, its evidence and the stored files for good,
       * which is why it carries the evidence-aware confirmation variant.
       */
      delete: {
        label: string
        /** The detail page spells the subject out, away from the row that named it. */
        detail_label: string
        confirm_title: string
        confirm_content: string
        /** Names what the delete destroys; takes the rendered `evidence` list. */
        confirm_content_with_evidence: string
        /**
         * Singular and plural spelled out as separate keys rather than through
         * polyglot's smart_count, so every leaf stays a typed string and a
         * missing locale entry fails type-check.
         */
        photo_one: string
        photo_many: string
        seal_one: string
        seal_many: string
        join: string
        success: string
        error: string
      }
      /** Heading of the detail-page group for seals still awaiting classification. */
      pending_seals: string
      /** The per-step upload on a live inspection's details page. */
      upload_evidence: {
        label: string
        uploading: string
        /** Singular and plural as separate keys, like `delete`. */
        success_one: string
        success_many: string
        /** Take the offending file's `name`. */
        unsupported_type: string
        too_large: string
        unreadable: string
        /** Takes the file's `name` and the server's `message`. */
        failed: string
      }
    }
    historical_inspections: {
      download_all: string
      add_photos: string
      add_seals: string
      add_evidence: {
        dialog_title_photos: string
        dialog_title_seals: string
        category: string
        serial_id: string
        owner: string
        select_files: string
        select_file: string
        upload: string
        cancel: string
        uploading: string
        success: string
        error: string
        no_files: string
      }
      delete: {
        label: string
        detail_label: string
        confirm_title: string
        confirm_content: string
        success: string
        error: string
      }
    }
  }
  resources: {
    inspections: {
      name: string
      load_error: string
      skipped_rows: string
      result_count: string
      fields: {
        container: string
        day: string
        location: string
        inspection_level: string
        services: string
        state: string
        manual: string
        steps: string
      }
      filters: {
        any: string
        clear: string
      }
      /**
       * The CSV export of the table. The columns the table shows reuse its
       * headers; these are the headers of the columns it has no header for,
       * and the yes/no words the manual flag becomes on paper.
       */
      export: {
        fields: {
          manual: string
          sostrat: string
          status: string
          photos: string
          seals: string
        }
        yes: string
        no: string
      }
      /**
       * Alt text of the thumbnails the evidence step strip shows on hover: a
       * photo is numbered within its step, a seal is named and carries its
       * serial number after the label when it has one.
       */
      evidence_preview: {
        photo: string
        seal: string
      }
      enums: {
        state: {
          PLANNED: string
          IN_PROGRESS: string
          FINISHED: string
          CANCELLED: string
        }
        /**
         * Raw SOSTRAT statuses, sentence-cased, appended to the state label of
         * a PIF row. Typed as an exhaustive record so a status added to the
         * database enum fails the build here rather than rendering blank.
         */
        status: Record<QualifiedStatusId, string>
        step_status: {
          'done-complete': string
          'done-missing': string
          active: string
          todo: string
        }
        origin: {
          MANUAL: string
          MANUAL_SYNCED: string
          AUTOMATIC: string
        }
      }
    }
    historical_inspections: {
      name: string
      timeline: {
        no_evidence: string
        no_steps: string
      }
      fields: {
        container: string
        day: string
        seals: string
        photos: string
        category: string
        owner: string
        location: string
        inspection_level: string
        services: string
        completeness: string
      }
      export: {
        fields: {
          day_of_month: string
          completeness: string
        }
      }
      enums: {
        location: Record<InspectionLocation, string>
        completeness: {
          COMPLETE: string
          INCOMPLETE: string
          OTHER: string
        }
        inspection_level: {
          AL: string
          BI: string
          BU: string
          DT: string
          ES: string
          FU: string
          ID: string
          IN: string
          NI: string
          PA: string
          PR: string
          RP: string
          SI: string
          TD: string
          TR_TO: string
          TR_TD: string
          TR_PO: string
          TR_PD: string
          VE: string
          VF: string
        }
        /**
         * Short labels for the Nivel chip, which shows the level without a
         * hover. A code that reads for itself maps to the code; the four
         * transvase codes map to a label that separates origin from destination
         * and total from partial, which `TR_TO` and its siblings do not. Typed
         * as an exhaustive record so a level added to the database enum fails
         * the build here rather than rendering blank.
         */
        inspection_level_short: Record<InspectionLevel, string>
        inspection_step: {
          POSICIONADO: string
          PRECINTOS_EXISTENTES: string
          APERTURA: string
          PASILLO: string
          MERCANCIA_EXTRAIDA: string
          CIERRE: string
          CONTENEDOR_CERRADO: string
          PRECINTOS_CIERRE: string
          OTROS: string
        }
        seal_category: {
          ORIGINAL: string
          NAVIERA: string
          TERMINAL: string
          EQ: string
          OUTRO: string
        }
        photo_category: {
          CONTAINER: string
          OPENING: string
          HALLWAYS: string
          EXTRACTED_MERCH: string
          LABEL_EQ: string
          CLOSING: string
          OTHERS: string
        }
      }
    }
  }
}

export type TranslationKey = RecursiveKeyOf<Translations>

type RecursiveKeyOf<TObj extends object> = {
  [TKey in keyof TObj & (string | number)]: TObj[TKey] extends object ? `${TKey}` | `${TKey}.${RecursiveKeyOf<TObj[TKey]>}` : `${TKey}`
}[keyof TObj & (string | number)]

export const translate = (key: TranslationKey, translations: Translations): string => {
  const segments = key.split('.')
  let current: unknown = translations
  for (const segment of segments) {
    if (typeof current !== 'object' || current === null) {
      return key
    }
    current = (current as Record<string, unknown>)[segment]
    if (current === undefined) {
      return key
    }
  }
  if (typeof current === 'string') {
    return current
  }
  return key
}

export const getTranslate = (locale: Locale): ((key: TranslationKey) => string) => {
  const translations = getTranslations(locale)
  return (key: TranslationKey) => translate(key, translations)
}

export const es: Translations = {
  ...spanishMessages,
  common: {
    back: 'Volver',
  },
  pages: {
    inspections: {
      delete: {
        label: 'Eliminar',
        detail_label: 'Eliminar inspección',
        confirm_title: 'Eliminar inspección',
        confirm_content: '¿Seguro que quieres eliminar esta inspección?',
        confirm_content_with_evidence:
          'Esta inspección contiene %{evidence}. Al eliminarla se borrará todo de forma permanente. ¿Eliminar de todos modos?',
        photo_one: '%{count} foto',
        photo_many: '%{count} fotos',
        seal_one: '%{count} precinto',
        seal_many: '%{count} precintos',
        join: ' y ',
        success: 'Inspección eliminada',
        error: 'Error al eliminar la inspección',
      },
      pending_seals: 'Precintos pendientes de clasificar',
      upload_evidence: {
        label: 'Subir imágenes',
        uploading: 'Subiendo…',
        success_one: '1 imagen subida',
        success_many: '%{count} imágenes subidas',
        unsupported_type: '«%{name}» no es un tipo de imagen admitido',
        too_large: '«%{name}» supera el tamaño máximo permitido',
        unreadable: '«%{name}» no se puede leer como imagen',
        failed: 'No se pudo subir «%{name}»: %{message}',
      },
    },
    historical_inspections: {
      download_all: 'Descargar Todas',
      add_photos: 'Añadir Fotos',
      add_seals: 'Añadir Precintos',
      add_evidence: {
        dialog_title_photos: 'Añadir fotos a la inspección',
        dialog_title_seals: 'Añadir precintos a la inspección',
        category: 'Categoría',
        serial_id: 'Número de serie',
        owner: 'Propietario',
        select_files: 'Seleccionar archivos',
        select_file: 'Seleccionar archivo',
        upload: 'Subir',
        cancel: 'Cancelar',
        uploading: 'Subiendo...',
        success: 'Evidencia añadida correctamente',
        error: 'Error al añadir evidencia',
        no_files: 'No se han seleccionado archivos',
      },
      delete: {
        label: 'Eliminar',
        detail_label: 'Eliminar inspección',
        confirm_title: 'Eliminar inspección',
        confirm_content: '¿Seguro que quieres eliminar esta inspección?',
        success: 'Inspección eliminada',
        error: 'Error al eliminar la inspección',
      },
    },
  },
  resources: {
    inspections: {
      name: 'Operativa',
      load_error: 'No se han podido cargar las inspecciones',
      skipped_rows: '%{count} inspección(es) no se han podido interpretar y se han omitido',
      result_count: 'Mostrando %{count} de %{total} inspecciones',
      fields: {
        container: 'Contenedor',
        day: 'Fecha',
        location: 'Ubicación',
        inspection_level: 'Nivel',
        services: 'Servicios',
        state: 'Estado',
        manual: 'Origen',
        steps: 'Evidencias',
      },
      filters: {
        any: 'Todos',
        clear: 'Limpiar filtros (%{count})',
      },
      export: {
        fields: {
          manual: 'Manual',
          sostrat: 'Sincronizada con SOSTRAT',
          status: 'Estado SOSTRAT',
          photos: 'Número de fotos',
          seals: 'Número de precintos',
        },
        yes: 'Sí',
        no: 'No',
      },
      evidence_preview: {
        photo: 'Foto %{index}',
        seal: 'Precinto',
      },
      enums: {
        state: {
          PLANNED: 'Planificada',
          IN_PROGRESS: 'En curso',
          FINISHED: 'Finalizada',
          CANCELLED: 'Anulada',
        },
        status: {
          CREADO: 'Creado',
          'EN EL PARKING': 'En el parking',
          'ASIGNADO A FINGER': 'Asignado a finger',
          'LLEGADA AL FINGER': 'Llegada al finger',
          'DESCARGA INICIADA': 'Descarga iniciada',
          'DESCARGA FINALIZADA': 'Descarga finalizada',
          'CARGA INICIADA': 'Carga iniciada',
          'CARGA FINALIZADA': 'Carga finalizada',
          PRECINTADO: 'Precintado',
          'PARKING FINAL': 'Parking final',
        },
        step_status: {
          'done-complete': 'Con evidencias',
          'done-missing': 'Sin evidencias',
          active: 'En curso',
          todo: 'Pendiente',
        },
        origin: {
          MANUAL: 'Manual',
          MANUAL_SYNCED: 'Manual (sincronizada)',
          AUTOMATIC: 'Automática',
        },
      },
    },
    historical_inspections: {
      name: 'Inspecciones',
      timeline: {
        no_evidence: 'No hay evidencias disponibles para esta inspección',
        no_steps: 'No hay pasos disponibles para esta inspección',
      },
      fields: {
        container: 'Contenedor',
        day: 'Fecha',
        seals: 'Precintos',
        photos: 'Fotos',
        category: 'Categoría',
        owner: 'Propietario',
        location: 'Ubicación',
        inspection_level: 'Nivel',
        services: 'Servicios',
        completeness: 'Evidencias',
      },
      export: {
        fields: {
          day_of_month: 'Día',
          completeness: 'Completo',
        },
      },
      enums: {
        location: {
          APMT: 'APMT',
          BEST: 'BEST',
          PIF: 'PIF',
          PORTNOU: 'PORTNOU',
        },
        completeness: {
          COMPLETE: 'Completo',
          INCOMPLETE: 'Incompleto',
          OTHER: 'Otros',
        },
        inspection_level: {
          AL: 'OTROS',
          BI: 'VACIADO',
          BU: 'VACIADO Y CLASIFICADO',
          DT: 'DEVOLUCIÓN A TERMINAL',
          ES: 'ESCANER',
          FU: 'FUMIGACIÓN',
          ID: 'IDENTIDAD',
          IN: 'INSPECCIÓN',
          NI: 'ABIERTO, PERO NO INSPECCIONADO',
          PA: 'PASILLO',
          PR: 'PRECINTADO',
          RP: 'REPRECINTADO, PERO NO ABIERTO',
          SI: 'ABIERTO Y FIRMADO',
          TD: 'TERMODESINFECCIÓN',
          TR_TO: 'TRANSVASE TOTAL ORIGEN',
          TR_TD: 'TRANSVASE TOTAL DESTINO',
          TR_PO: 'TRANSVASE PARCIAL ORIGEN',
          TR_PD: 'TRANSVASE PARCIAL DESTINO',
          VE: 'VENTILACIÓN',
          VF: 'VERIFICACIÓN DE FUMIGACIÓN',
        },
        inspection_level_short: {
          AL: 'AL',
          BI: 'BI',
          BU: 'BU',
          DT: 'DT',
          ES: 'ES',
          FU: 'FU',
          ID: 'ID',
          IN: 'IN',
          NI: 'NI',
          PA: 'PA',
          PR: 'PR',
          RP: 'RP',
          SI: 'SI',
          TD: 'TD',
          TR_TO: 'TR (origen)',
          TR_TD: 'TR (destino)',
          TR_PO: 'TR parcial (origen)',
          TR_PD: 'TR parcial (destino)',
          VE: 'VE',
          VF: 'VF',
        },
        inspection_step: {
          POSICIONADO: 'Posicionado',
          PRECINTOS_EXISTENTES: 'Precintos existentes',
          APERTURA: 'Apertura',
          PASILLO: 'Pasillo',
          MERCANCIA_EXTRAIDA: 'Mercancía extraída',
          CIERRE: 'Cierre',
          CONTENEDOR_CERRADO: 'Contenedor cerrado',
          PRECINTOS_CIERRE: 'Precintos cierre',
          OTROS: 'Otros',
        },
        seal_category: {
          ORIGINAL: 'Origen',
          NAVIERA: 'Naviera',
          TERMINAL: 'Terminal',
          EQ: 'EQ',
          OUTRO: 'Otros',
        },
        photo_category: {
          CONTAINER: 'Posicionado',
          OPENING: 'Apertura',
          HALLWAYS: 'Pasillo',
          EXTRACTED_MERCH: 'Mercancía extraída',
          LABEL_EQ: 'Cierre',
          CLOSING: 'Contenedor cerrado',
          OTHERS: 'Otros',
        },
      },
    },
  },
}

export const ca: Translations = {
  ...catalanMessages,
  common: {
    back: 'Tornar',
  },
  pages: {
    inspections: {
      delete: {
        label: 'Eliminar',
        detail_label: 'Eliminar inspecció',
        confirm_title: 'Eliminar inspecció',
        confirm_content: 'Segur que vols eliminar aquesta inspecció?',
        confirm_content_with_evidence:
          'Aquesta inspecció conté %{evidence}. En eliminar-la s’esborrarà tot de manera permanent. Segur que vols eliminar-la?',
        photo_one: '%{count} foto',
        photo_many: '%{count} fotos',
        seal_one: '%{count} precinte',
        seal_many: '%{count} precintes',
        join: ' i ',
        success: 'Inspecció eliminada',
        error: 'Error en eliminar la inspecció',
      },
      pending_seals: 'Precintes pendents de classificar',
      upload_evidence: {
        label: 'Pujar imatges',
        uploading: 'Pujant…',
        success_one: '1 imatge pujada',
        success_many: '%{count} imatges pujades',
        unsupported_type: "«%{name}» no és un tipus d'imatge admès",
        too_large: '«%{name}» supera la mida màxima permesa',
        unreadable: '«%{name}» no es pot llegir com a imatge',
        failed: "No s'ha pogut pujar «%{name}»: %{message}",
      },
    },
    historical_inspections: {
      download_all: 'Descarregar Totes',
      add_photos: 'Afegir Fotos',
      add_seals: 'Afegir Precintes',
      add_evidence: {
        dialog_title_photos: 'Afegir fotos a la inspecció',
        dialog_title_seals: 'Afegir precintes a la inspecció',
        category: 'Categoria',
        serial_id: 'Número de sèrie',
        owner: 'Propietari',
        select_files: 'Seleccionar fitxers',
        select_file: 'Seleccionar fitxer',
        upload: 'Pujar',
        cancel: 'Cancel·lar',
        uploading: 'Pujant...',
        success: 'Evidència afegida correctament',
        error: 'Error en afegir evidència',
        no_files: "No s'han seleccionat fitxers",
      },
      delete: {
        label: 'Eliminar',
        detail_label: 'Eliminar inspecció',
        confirm_title: 'Eliminar inspecció',
        confirm_content: 'Segur que vols eliminar aquesta inspecció?',
        success: 'Inspecció eliminada',
        error: 'Error en eliminar la inspecció',
      },
    },
  },
  resources: {
    inspections: {
      name: 'Operativa',
      load_error: 'No s’han pogut carregar les inspeccions',
      skipped_rows: '%{count} inspecció(ns) no s’han pogut interpretar i s’han omès',
      result_count: 'Mostrant %{count} de %{total} inspeccions',
      fields: {
        container: 'Contenidor',
        day: 'Data',
        location: 'Ubicació',
        inspection_level: 'Nivell',
        services: 'Serveis',
        state: 'Estat',
        manual: 'Origen',
        steps: 'Evidències',
      },
      filters: {
        any: 'Tots',
        clear: 'Netejar filtres (%{count})',
      },
      export: {
        fields: {
          manual: 'Manual',
          sostrat: 'Sincronitzada amb SOSTRAT',
          status: 'Estat SOSTRAT',
          photos: 'Nombre de fotos',
          seals: 'Nombre de precintes',
        },
        yes: 'Sí',
        no: 'No',
      },
      evidence_preview: {
        photo: 'Foto %{index}',
        seal: 'Precinte',
      },
      enums: {
        state: {
          PLANNED: 'Planificada',
          IN_PROGRESS: 'En curs',
          FINISHED: 'Finalitzada',
          CANCELLED: 'Anul·lada',
        },
        status: {
          CREADO: 'Creat',
          'EN EL PARKING': 'Al pàrquing',
          'ASIGNADO A FINGER': 'Assignat a finger',
          'LLEGADA AL FINGER': 'Arribada al finger',
          'DESCARGA INICIADA': 'Descàrrega iniciada',
          'DESCARGA FINALIZADA': 'Descàrrega finalitzada',
          'CARGA INICIADA': 'Càrrega iniciada',
          'CARGA FINALIZADA': 'Càrrega finalitzada',
          PRECINTADO: 'Precintat',
          'PARKING FINAL': 'Pàrquing final',
        },
        step_status: {
          'done-complete': 'Amb evidències',
          'done-missing': 'Sense evidències',
          active: 'En curs',
          todo: 'Pendent',
        },
        origin: {
          MANUAL: 'Manual',
          MANUAL_SYNCED: 'Manual (sincronitzada)',
          AUTOMATIC: 'Automàtica',
        },
      },
    },
    historical_inspections: {
      name: 'Inspeccions',
      timeline: {
        no_evidence: 'No hi ha evidències disponibles per a aquesta inspecció',
        no_steps: 'No hi ha passos disponibles per a aquesta inspecció',
      },
      fields: {
        container: 'Contenidor',
        day: 'Data',
        seals: 'Precintes',
        photos: 'Fotos',
        category: 'Categoria',
        owner: 'Propietari',
        location: 'Ubicació',
        inspection_level: 'Nivell',
        services: 'Serveis',
        completeness: 'Evidències',
      },
      export: {
        fields: {
          day_of_month: 'Dia',
          completeness: 'Complet',
        },
      },
      enums: {
        location: {
          APMT: 'APMT',
          BEST: 'BEST',
          PIF: 'PIF',
          PORTNOU: 'PORTNOU',
        },
        completeness: {
          COMPLETE: 'Complet',
          INCOMPLETE: 'Incomplet',
          OTHER: 'Altres',
        },
        inspection_level: {
          AL: 'ALTRES',
          BI: 'BUIDAT',
          BU: 'BUIDAT I CLASSIFICAT',
          DT: 'DEVOLUCIÓ A TERMINAL',
          ES: 'ESCÀNER',
          FU: 'FUMIGACIÓ',
          ID: 'IDENTITAT',
          IN: 'INSPECCIÓ',
          NI: 'OBERT, PERÒ NO INSPECCIONAT',
          PA: 'PASSADÍS',
          PR: 'PRECINTAT',
          RP: 'REPRECINTAT, PERÒ NO OBERT',
          SI: 'OBERT I SIGNAT',
          TD: 'TERMODESINFECCIÓ',
          TR_TO: 'TRANSVASAMENT TOTAL ORIGEN',
          TR_TD: 'TRANSVASAMENT TOTAL DESTÍ',
          TR_PO: 'TRANSVASAMENT PARCIAL ORIGEN',
          TR_PD: 'TRANSVASAMENT PARCIAL DESTÍ',
          VE: 'VENTILACIÓ',
          VF: 'VERIFICACIÓ DE LA FUMIGACIÓ',
        },
        inspection_level_short: {
          AL: 'AL',
          BI: 'BI',
          BU: 'BU',
          DT: 'DT',
          ES: 'ES',
          FU: 'FU',
          ID: 'ID',
          IN: 'IN',
          NI: 'NI',
          PA: 'PA',
          PR: 'PR',
          RP: 'RP',
          SI: 'SI',
          TD: 'TD',
          TR_TO: 'TR (origen)',
          TR_TD: 'TR (destí)',
          TR_PO: 'TR parcial (origen)',
          TR_PD: 'TR parcial (destí)',
          VE: 'VE',
          VF: 'VF',
        },
        inspection_step: {
          POSICIONADO: 'Posicionat',
          PRECINTOS_EXISTENTES: 'Precintes existents',
          APERTURA: 'Obertura',
          PASILLO: 'Passadís',
          MERCANCIA_EXTRAIDA: 'Mercaderia extreta',
          CIERRE: 'Tancament',
          CONTENEDOR_CERRADO: 'Contenidor tancat',
          PRECINTOS_CIERRE: 'Precintes tancament',
          OTROS: 'Altres',
        },
        seal_category: {
          ORIGINAL: 'Origen',
          NAVIERA: 'Naviliera',
          TERMINAL: 'Terminal',
          EQ: 'EQ',
          OUTRO: 'Altres',
        },
        photo_category: {
          CONTAINER: 'Posicionat',
          OPENING: 'Obertura',
          HALLWAYS: 'Passadís',
          EXTRACTED_MERCH: 'Mercaderia extreta',
          LABEL_EQ: 'Tancament',
          CLOSING: 'Contenidor tancat',
          OTHERS: 'Altres',
        },
      },
    },
  },
}

export const i18nProvider: I18nProvider = polyglotI18nProvider((locale: string) => getTranslations(locale), 'es', availableLocales)
